/**
 * Deterministic sensitivity filter — the FLOOR of the cross-vendor egress gate
 * (decision 2026-06-20: cross-model verification may send a candidate to a SECOND
 * vendor only when EVERY gate layer passes; this layer does NOT trust the model's
 * own judgment). Pure + deliberately CONSERVATIVE: it flags anything that looks like
 * a secret, credential, or personal identifier so it never leaves the host.
 *
 * Fail toward flagging: a false "sensitive" only costs a candidate its cross-model
 * upgrade (it stays self/LOW — cheap, correctable), while a false "safe" would leak
 * the user's data to another vendor (expensive, irreversible). That asymmetry is the
 * whole point — "민감도 게이트는 확실하게".
 */

export interface SensitivityVerdict {
  /** True = must NOT leave the host (any rule matched). */
  readonly sensitive: boolean;
  /** Which category labels matched — for transparency + tests. */
  readonly reasons: readonly string[];
}

/**
 * Category → pattern. Specific shapes only (no generic high-entropy heuristic, which
 * would false-positive on object ids / hashes and starve the feature). Each is anchored
 * loosely on purpose; over-matching is the safe direction here.
 */
const RULES: ReadonlyArray<readonly [string, RegExp]> = [
  // ── secrets / credentials ──
  ["private-key", /-----BEGIN (?:RSA |EC |OPENSSH |PGP |DSA )?PRIVATE KEY-----/i],
  ["jwt", /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/],
  ["openai-key", /\bsk-(?:proj-|ant-)?[A-Za-z0-9_-]{20,}\b/],
  ["stripe-key", /\b[sr]k_(?:live|test)_[A-Za-z0-9]{16,}\b/],
  ["sendgrid-key", /\bSG\.[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}\b/],
  ["gitlab-token", /\bglpat-[A-Za-z0-9_-]{16,}\b/],
  ["github-token", /\bgh[pousr]_[A-Za-z0-9]{20,}\b/],
  ["npm-token", /\bnpm_[A-Za-z0-9]{30,}\b/],
  ["aws-access-key", /\bAKIA[0-9A-Z]{16}\b/],
  ["slack-token", /\bxox[baprs]-[A-Za-z0-9-]{10,}\b/],
  ["google-api-key", /\bAIza[0-9A-Za-z_-]{30,}\b/],
  ["bearer-token", /\b[Bb]earer\s+[A-Za-z0-9._~+/-]{12,}=*/],
  ["authorization-header", /\bAuthorization\s*[:=]/i],
  // key:value or key=value, with optional quotes around key AND value (covers JSON
  // "password": "x" and camelCase apiKey/accessToken/clientSecret/privateKey/etc).
  ["secret-assignment", /["']?(?:pass(?:word|wd|phrase)?|secret|api[_-]?key|access[_-]?(?:key|token)|client[_-]?secret|auth[_-]?token|session[_-]?token|refresh[_-]?token|private[_-]?key|credential|token)["']?\s*[:=]\s*["']?\S/i],
  // scheme://user:pass@host — credentials embedded in a URL.
  ["credential-url", /[a-z][a-z0-9+.-]*:\/\/[^/\s:@]+:[^/\s@]+@/i],
  // natural-language credential disclosure — a credential noun/phrase (EN/KO) joined to a
  // value by a copula/separator. The `secret-assignment` rule above only catches machine
  // key:value / key=value with English keys; this catches the way a person SPEAKS a secret
  // in chat — "the admin password is hunter2", "비밀번호는 1q2w3e", "access token is abc123",
  // "API 키는 abc123". A CONNECTOR is required (en copula / KO particle 은·는·이·가 / : = ->),
  // so a bare mention with no value ("password reset flow", "비밀번호를 잊었다") does NOT match.
  // "password is X" rarely means anything but a disclosure, so password/pin/code nouns
  // flag on ANY value after the connector.
  ["nl-credential", /(?:\bpass(?:word|wd|phrase|code)\b|\b(?:pin|otp|2fa|mfa)\b|(?:recovery|backup|security|pin|verification)[\s-]?code\b|비밀번호|비번|암호|패스워드|인증번호|보안\s?코드|복구\s?코드|백업\s?코드)\s*(?:[:=]|->|은|는|이|가|\bis\b|\bwas\b|\bequals\b)\s*\S/iu],
  // multi-word key/token/secret phrases are idiom-prone ("API token is expired", "the cache
  // key is wrong"), so they flag ONLY when the value looks secret-y (contains a digit). This
  // catches "access token is abc123" / "API 키는 abc123" but spares dev chatter about tokens.
  // Single generic words (key/token/secret) are excluded entirely (Codex round-2, FP-bounded).
  ["nl-credential-phrase", /(?:(?:api|access|refresh|secret|private|auth|session|client)[\s-]?(?:key|token|secret)|액세스\s?토큰|api\s?키|시크릿\s?키|비밀\s?키|인증\s?토큰)\s*(?:[:=]|->|은|는|이|가|\bis\b|\bwas\b|\bequals\b)\s*(?=\S*\d)\S{2,}/iu],
  // one-time / verification CODE spoken with its digits — "OTP 482913", "OTP 482 913",
  // "인증번호: 8842", "verification code 123456". The long-digit-run rule only fires at 9+
  // digits, so a 4-8 digit OTP/PIN would slip through; a code-noun + 4+ digits (allowing a
  // single space/dash between digits) closes it.
  ["otp-code", /(?:\b(?:otp|pin|2fa|mfa)\b|인증\s?번호|보안\s?코드|복구\s?코드|백업\s?코드|일회용\s?비밀번호|verification\s?code|one[-\s]?time\s?(?:password|code)|auth(?:entication)?\s?code|recovery\s?code|backup\s?code|security\s?code|pin\s?code)\s*[:=-]?\s*\d(?:[\s-]?\d){3,}/iu],
  // plain note style — a password noun directly before a value that CONTAINS A DIGIT, with
  // no copula ("비번 hunter2", "pw 1q2w3e", "암호 - 9xK2"). The digit requirement is what keeps
  // benign phrases out ("비밀번호 정책", "password reset"): those values have no digit.
  ["credential-note", /(?:\bpass(?:word|wd)\b|\bpw\b|\bpwd\b|비밀번호|비번|암호|패스워드)\s*[-:=]?\s+(?=\S*\d)\S{3,}/iu],
  // long opaque token: 40+ base64/hex run (catches prefix-less secrets + hashes; may
  // also flag a sha — acceptable, fail toward blocking).
  ["high-entropy-token", /\b[A-Za-z0-9+/_-]{40,}={0,2}\b/],
  // ── personal identifiers ──
  ["email", /[^\s@()<>]+@[^\s@()<>]+\.[^\s@()<>]{2,}/u],
  ["obfuscated-email", /\S+\s*[[(]\s*at\s*[\])]\s*\S+\s*[[(]\s*dot\s*[\])]/i],
  ["long-digit-run", /\b(?:\d[ .-]?){9,19}\b/], // SSN(9) / phone(10-11) / card(13-19) / numeric id
  ["us-ssn", /\b\d{3}-\d{2}-\d{4}\b/],
  ["kr-rrn", /\b\d{6}-[1-8]\d{6}\b/], // Korean resident registration number (7th digit 1-8 incl. foreigners)
];

/** Scan one or more text fragments; sensitive if ANY rule matches ANY fragment. */
export function classifySensitivity(...texts: (string | null | undefined)[]): SensitivityVerdict {
  const hay = texts.filter((t): t is string => typeof t === "string" && t.length > 0).join("\n");
  if (!hay) return { sensitive: false, reasons: [] };
  const reasons: string[] = [];
  for (const [label, re] of RULES) {
    // Reset lastIndex defensively (none are /g, but be safe if a rule gains the flag).
    re.lastIndex = 0;
    if (re.test(hay)) reasons.push(label);
  }
  return { sensitive: reasons.length > 0, reasons };
}

/**
 * Sensitivity of an extraction CANDIDATE: its verbatim quote (the main risk surface)
 * plus the triple's refs/labels. This is what the egress gate calls — a sensitive
 * verdict keeps the candidate on the host (self/LOW), never cross-vendor.
 */
export function candidateSensitivity(c: {
  readonly quote?: string | null;
  readonly sourceRef?: string | null;
  readonly rel?: string | null;
  readonly targetRef?: string | null;
  readonly labels?: readonly string[];
}): SensitivityVerdict {
  return classifySensitivity(c.quote, c.sourceRef, c.rel, c.targetRef, ...(c.labels ?? []));
}

/** Max chars of an anchor quote that may ever leave the host (data minimization). */
export const QUOTE_EGRESS_CAP = 240;
/** Max chars of a triple ref/rel that may leave the host (canonical ids are short). */
export const REF_EGRESS_CAP = 160;

export interface EgressGateInput {
  /** Config opt-in (layer 1) — false ⇒ never egress. */
  readonly crossVendorEgress: boolean;
  /** Extractor's own sensitive flag (layer 3). */
  readonly modelSensitive?: boolean;
  readonly quote?: string | null;
  readonly sourceRef?: string | null;
  readonly rel?: string | null;
  readonly targetRef?: string | null;
  readonly labels?: readonly string[];
}

export interface EgressDecision {
  readonly eligible: boolean;
  /** Every reason it was blocked (empty ⇒ eligible) — for transparency + tests. */
  readonly blockedBy: readonly string[];
}

/**
 * The cross-vendor egress gate — ALL layers must pass for a candidate fragment to be
 * sent to a second vendor (decision 2026-06-20, "민감도 게이트 확실하게"). Fail CLOSED:
 *   1. config `crossVendorEgress` must be on,
 *   2. deterministic filter must find nothing (the floor — model not trusted),
 *   3. the extractor's own `modelSensitive` flag must be false,
 *   4. there must BE an anchor quote and it must be within the size cap.
 * A block on any layer ⇒ the candidate stays on the host (self/LOW), never egress.
 */
export function crossVendorEgressEligible(input: EgressGateInput): EgressDecision {
  const blockedBy: string[] = [];
  if (!input.crossVendorEgress) blockedBy.push("egress-disabled");
  // Layer 3 fail-closed: the extractor must AFFIRMATIVELY clear it (sensitive === false).
  // A missing judgment (undefined) is NOT a clearance — it blocks (Codex 2026-06-20).
  if (input.modelSensitive !== false) blockedBy.push("not-affirmed-nonsensitive");
  const det = candidateSensitivity(input);
  if (det.sensitive) blockedBy.push(...det.reasons.map((r) => `deterministic:${r}`));
  const q = (input.quote ?? "").trim();
  if (!q) blockedBy.push("no-quote"); // nothing anchorable ⇒ nothing safe to verify
  else if ((input.quote ?? "").length > QUOTE_EGRESS_CAP) blockedBy.push("quote-too-long");
  // A triple ref longer than this is not a canonical id — refuse to egress user text smuggled into it.
  if ((input.sourceRef ?? "").length > REF_EGRESS_CAP || (input.targetRef ?? "").length > REF_EGRESS_CAP || (input.rel ?? "").length > REF_EGRESS_CAP)
    blockedBy.push("ref-too-long");
  return { eligible: blockedBy.length === 0, blockedBy };
}

/** The MINIMAL payload that may leave the host — only the triple + the capped quote. */
export interface EgressPayload {
  readonly sourceRef: string;
  readonly rel: string;
  readonly targetRef: string;
  readonly quote: string;
}

/**
 * Build the minimized egress payload — SELF-ENFORCING: returns null for a candidate the
 * gate would block, so the helper cannot be misused to send ineligible data (no "I
 * forgot to check eligibility first" footgun — Codex 2026-06-20). On eligible input it
 * carries nothing but the triple and the quote, EVERY field hard-capped, never messages
 * / transcript / prior summary.
 */
export function egressPayload(input: EgressGateInput): EgressPayload | null {
  if (!crossVendorEgressEligible(input).eligible) return null;
  return {
    sourceRef: (input.sourceRef ?? "").slice(0, REF_EGRESS_CAP),
    rel: (input.rel ?? "").slice(0, REF_EGRESS_CAP),
    targetRef: (input.targetRef ?? "").slice(0, REF_EGRESS_CAP),
    quote: (input.quote ?? "").slice(0, QUOTE_EGRESS_CAP),
  };
}

/**
 * Ingest-time egress-gate enforcement, shared by every ingest path (record + conversation):
 * honor a `cross-model` verdict ONLY when egress was on AND the extractor affirmatively
 * cleared it (`sensitive === false`) AND the deterministic floor finds nothing. Otherwise
 * downgrade to `self` — a sensitive or egress-off candidate can never reach cross-model/
 * MEDIUM, whatever the agent claimed. The code-level safety net under the agent's
 * pre-egress gate.
 */
export function gatedVerifier(
  verifier: "cross-model" | "self",
  modelSensitive: boolean | undefined,
  crossVendorEgress: boolean,
  texts: {
    readonly quote?: string | null;
    readonly sourceRef?: string | null;
    readonly rel?: string | null;
    readonly targetRef?: string | null;
    readonly labels?: readonly string[];
  },
): "cross-model" | "self" {
  if (verifier !== "cross-model") return "self";
  // DELEGATE to the one gate, so ingest enforcement is byte-identical to the pre-egress
  // check — including no-quote / quote-too-long / ref-too-long. A candidate that
  // `egressPayload` would refuse can never be honored as cross-model (Codex round-3).
  return crossVendorEgressEligible({ crossVendorEgress, modelSensitive, ...texts }).eligible
    ? "cross-model"
    : "self";
}
